/**
 * useSkillVersionNudge — informational MIN_SKILL_VERSION toast.
 *
 * Sibling of `useCliVersionNudge`. Where that nudge tracks the `ano` CLI
 * binary, this one tracks the `@ano-chat/skills` Claude Code plugin — the
 * channel that delivers `/unread`, the ano-messages/ano-channels skills,
 * etc. The two are independent: a user can have a current CLI and a stale
 * plugin (or vice-versa), so each gets its own probe + toast.
 *
 * Fires once per workspace mount when the locally installed plugin is
 * below the server-published minimum, as a sticky Sonner toast
 * (`duration: Infinity`) with an "Update" action that types the upgrade
 * command into the Shell. Dismissed via X → remembered in localStorage
 * keyed by the violation's `minVersion`, so a fresh server bump re-shows
 * the toast even if the previous one was dismissed.
 *
 * Web runtime: no-op (no local plugin install there).
 * Electron + plugin not installed: fires when `MIN_SKILL_VERSION > 0.0.0`.
 * Electron + plugin installed and current: no-op.
 */
import { useEffect, useRef } from "react";
import { toast } from "sonner";

import { isElectronRuntime } from "@/lib/logout";
import { API_URL } from "@/lib/config";
import { checkMinSkillVersion } from "@/lib/min-skill-version-check";

declare global {
  interface Window {
    // Kept in lockstep with the declarations in useCliVersionNudge.ts and
    // useAnoMcpNudge.ts — TypeScript merges these partial interfaces, so
    // all three must expose the same inner shape or tsc fails with
    // "subsequent property declarations must have the same type".
    __electronApi?: {
      anoCliGetVersion?: () => Promise<{
        version: string | null;
        error?: string;
      }>;
      anoSkillGetVersion?: () => Promise<{
        version: string | null;
        error?: string;
      }>;
      claudeCheckAnoMcp?: () => Promise<{
        status:
          | "connected"
          | "needs_auth"
          | "missing"
          | "claude_missing"
          | "unknown";
        url?: string;
        error?: string;
      }>;
    };
  }
}

const TOAST_ID = "skill-version-nudge";
const DISMISS_STORAGE_KEY = "ano-skill-version-nudge-dismissed";

function isDismissedFor(minVersion: string): boolean {
  try {
    return localStorage.getItem(DISMISS_STORAGE_KEY) === minVersion;
  } catch {
    return false;
  }
}

function rememberDismissal(minVersion: string): void {
  try {
    localStorage.setItem(DISMISS_STORAGE_KEY, minVersion);
  } catch {
    /* ignore storage failures */
  }
}

function sendToShell(cmd: string): void {
  window.dispatchEvent(new CustomEvent("terminal:open"));
  window.dispatchEvent(
    new CustomEvent("terminal:rerun", {
      detail: { command: cmd, autoExecute: false },
    }),
  );
}

export function useSkillVersionNudge(enabled: boolean): void {
  const firedRef = useRef(false);

  useEffect(() => {
    if (!enabled || !isElectronRuntime() || firedRef.current) return;
    let cancelled = false;
    void (async () => {
      const probe = await window.__electronApi
        ?.anoSkillGetVersion?.()
        .catch(() => null);
      const currentVersion = probe?.version ?? null;
      const v = await checkMinSkillVersion({
        apiBaseUrl: API_URL,
        currentVersion,
      });
      if (cancelled || !v) return;
      if (isDismissedFor(v.minVersion)) return;
      firedRef.current = true;

      const isMissing = v.currentVersion === "(not installed)";
      toast.warning(
        isMissing
          ? "Add Ano Skills to Claude Code"
          : "Your Ano Skills plugin is out of date",
        {
          id: TOAST_ID,
          description: isMissing
            ? `Teaches Claude Code to triage unread channels (/unread), send messages, and share files. Requires plugin ${v.minVersion}+.`
            : `${v.currentVersion} → ${v.minVersion} adds the latest skills (unread triage, jump-to-message links, file attachments).`,
          duration: Infinity,
          closeButton: true,
          action: {
            label: isMissing ? "Install" : "Update",
            onClick: () => sendToShell(v.upgradeCommand),
          },
          onDismiss: () => rememberDismissal(v.minVersion),
        },
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled]);
}
